import { CommonModule } from '@angular/common';
import { Component, computed, effect, input, model, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';

import { ProducerStatus } from '@core/models/producer-admin';

@Component({
  selector: 'app-change-status-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, MessageModule, SelectModule],
  templateUrl: './change-status-dialog.html',
  styleUrl: './change-status-dialog.scss'
})
export class ChangeStatusDialog {
  readonly visible = model<boolean>(false);
  readonly producerName = input<string>('');
  readonly currentStatus = input.required<ProducerStatus>();
  readonly submitting = input<boolean>(false);

  readonly confirm = output<ProducerStatus>();
  readonly cancel = output<void>();

  readonly selectedStatus = signal<ProducerStatus | null>(null);

  readonly statusOptions = Object.values(ProducerStatus).map((value) => ({ label: value, value }));

  readonly ProducerStatus = ProducerStatus;

  readonly consequenceMessage = computed<string>(() => {
    const selected = this.selectedStatus();
    if (!selected || selected === this.currentStatus()) {
      return '';
    }
    return selected === ProducerStatus.Active
      ? "This producer's published menu items will become visible to consumers."
      : "This producer's menu items will be hidden from all consumer menus.";
  });

  readonly confirmDisabled = computed<boolean>(() => {
    const selected = this.selectedStatus();
    return !selected || selected === this.currentStatus();
  });

  constructor() {
    effect(() => {
      if (this.visible()) {
        const current = this.currentStatus();
        untracked(() => this.selectedStatus.set(current));
      }
    });
  }

  onConfirm(): void {
    const selected = this.selectedStatus();
    if (selected && selected !== this.currentStatus()) {
      this.confirm.emit(selected);
    }
  }

  onCancel(): void {
    this.cancel.emit();
    this.visible.set(false);
  }

  onVisibleChange(value: boolean): void {
    if (!value) {
      this.cancel.emit();
    }
    this.visible.set(value);
  }
}
